import { getAuthCookie } from "../../utils/authCookies";
import { showError, showSuccess } from "../../toast/toast";
import { markAsReadApi, NotificationItemType } from "./notificationMockData";

export const VIEW_MEMBERSHIP_ACTION = "VIEW_MEMBERSHIP";
export const ORGANIZATION_MEMBERSHIP_RESOURCE = "organization_membership";

export type MembershipClickResult = "prompt" | "access_required" | "noop";

export type MembershipViewAccessPrompt = {
  notificationId: string;
  membershipId: string;
  brokerageOrganizationId: string;
  message: string;
};

function asId(value: unknown): string {
  if (value == null) return "";
  if (typeof value === "string" || typeof value === "number") {
    return String(value).trim();
  }
  if (typeof value === "object") {
    const obj = value as Record<string, unknown>;
    return asId(obj.$oid ?? obj.hex ?? obj.id ?? obj._id ?? obj.value);
  }
  return "";
}

function asIdList(value: unknown): string[] {
  if (!value) return [];
  if (Array.isArray(value)) {
    return Array.from(new Set(value.map(asId).filter(Boolean)));
  }
  const single = asId(value);
  return single ? [single] : [];
}

function readMeta(item: NotificationItemType, key: string): unknown {
  const metadata = item.metadata;
  if (metadata && typeof metadata === "object") {
    return (metadata as Record<string, unknown>)[key];
  }
  return undefined;
}

function unwrapPayload(raw: unknown): Record<string, any> {
  if (!raw || typeof raw !== "object") return {};
  const body = raw as Record<string, any>;
  if (body.data && typeof body.data === "object" && !Array.isArray(body.data)) {
    return body.data as Record<string, any>;
  }
  return body;
}

function personName(membership: Record<string, any>): string {
  const first = String(membership.firstName || membership.first_name || "").trim();
  const last = String(membership.lastName || membership.last_name || "").trim();
  const full = `${first} ${last}`.trim();
  if (full) return full;
  const globalUser = membership.globalUser || membership.global_user || {};
  const fromGlobal = `${String(globalUser.firstName || globalUser.first_name || "").trim()} ${String(
    globalUser.lastName || globalUser.last_name || ""
  ).trim()}`.trim();
  return fromGlobal || String(membership.email || globalUser.email || "").trim() || "This person";
}

function organizationName(value: unknown, fallback = "another brokerage"): string {
  if (typeof value === "string" && value.trim()) return value.trim();
  if (value && typeof value === "object") {
    const org = value as Record<string, any>;
    const name = String(
      org.name || org.company_name || org.companyName || org.firmDetails?.companyName || ""
    ).trim();
    if (name) return name;
  }
  return fallback;
}

export function isViewMembershipNotification(item: NotificationItemType): boolean {
  const action = String(item.action || readMeta(item, "action") || "")
    .trim()
    .toUpperCase();
  if (action !== VIEW_MEMBERSHIP_ACTION) return false;

  const resourceType = String(
    item.related_resource_type || readMeta(item, "related_resource_type") || ""
  )
    .trim()
    .toLowerCase();

  return !resourceType || resourceType === ORGANIZATION_MEMBERSHIP_RESOURCE;
}

export function getRelatedMembershipIds(item: NotificationItemType): string[] {
  return asIdList(
    item.related_resource_ids ?? readMeta(item, "related_resource_ids")
  );
}

function buildAccessMessage(
  agentName: string,
  currentBrokerage: string,
  otherBrokerage: string
): string {
  return `${agentName} currently has an active license with ${currentBrokerage}. You're viewing their membership under ${otherBrokerage}. Would you like to request access to their membership with ${currentBrokerage}?`;
}

async function fetchJson(
  url: string,
  token: string,
  method: "GET" | "POST" = "GET"
): Promise<{ ok: boolean; status: number; data: Record<string, any> }> {
  const res = await fetch(url, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
    },
  });
  const data = unwrapPayload(await res.json().catch(() => ({})));
  return { ok: res.ok, status: res.status, data };
}

function parseLicensedBrokerageFromText(text: string): string {
  const match = String(text || "").match(/active license under\s+(.+?)\./i);
  return String(match?.[1] || "").trim();
}

function parsePersonFromText(text: string): string {
  const match = String(text || "").match(
    /^(?:Agent|Team member)\s+(.+?)\s+currently has an active license/i
  );
  return String(match?.[1] || "").trim();
}

function promptFromMembership(
  item: NotificationItemType,
  membershipId: string,
  membership: Record<string, any>
): MembershipViewAccessPrompt {
  const metadata =
    item.metadata && typeof item.metadata === "object" ? item.metadata : {};
  const organization = membership.organization || {};
  const licenseActivatedBy =
    membership.license_activated_by || membership.licenseActivatedBy || {};
  const otherBrokerageName =
    organizationName(metadata.other_brokerage_name, "") ||
    organizationName(organization, "") ||
    "another brokerage";
  const currentBrokerageName =
    organizationName(metadata.current_brokerage_name, "") ||
    organizationName(licenseActivatedBy.company_name || licenseActivatedBy, "") ||
    parseLicensedBrokerageFromText(item.description) ||
    "their current brokerage";
  const brokerageOrganizationId = asId(
    metadata.brokerage_organization_id ||
      metadata.current_brokerage_organization_id ||
      licenseActivatedBy.organization_id ||
      licenseActivatedBy.organizationId ||
      licenseActivatedBy._id ||
      licenseActivatedBy.id ||
      membership.organization_id ||
      organization._id ||
      organization.id
  );
  const agent =
    String(metadata.agent_name || "").trim() ||
    personName(membership) ||
    parsePersonFromText(item.description) ||
    "This person";

  return {
    notificationId: item.id,
    membershipId,
    brokerageOrganizationId,
    message: buildAccessMessage(agent, currentBrokerageName, otherBrokerageName),
  };
}

export async function prepareMembershipViewAccessPrompt(
  item: NotificationItemType
): Promise<{ result: MembershipClickResult; prompt?: MembershipViewAccessPrompt }> {
  if (!isViewMembershipNotification(item)) {
    return { result: "noop" };
  }

  const ids = getRelatedMembershipIds(item);
  if (!ids.length) {
    showError("This notification has no membership to open.");
    return { result: "noop" };
  }

  const token = getAuthCookie("sessionToken") || "";
  if (!token) {
    showError("Please sign in again to open this membership.");
    return { result: "noop" };
  }

  const apiBase = String(process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
  let accessRequired = false;
  let lastError = "";

  for (const membershipId of ids) {
    try {
      const basic = await fetchJson(
        `${apiBase}/organization-memberships/${encodeURIComponent(membershipId)}`,
        token
      );
      if (basic.status === 403) {
        accessRequired = true;
        lastError = basic.data?.error || basic.data?.message || "";
        continue;
      }
      if (!basic.ok) {
        lastError = basic.data?.error || basic.data?.message || "Failed to load membership.";
        continue;
      }

      let membership = basic.data;
      const details = await fetchJson(
        `${apiBase}/organization-memberships/${encodeURIComponent(membershipId)}/full-details`,
        token
      );
      if (details.ok) {
        membership = { ...membership, ...details.data };
      }

      return {
        result: "prompt",
        prompt: promptFromMembership(item, membershipId, membership),
      };
    } catch (error: any) {
      lastError = error?.message || "Failed to load membership.";
    }
  }

  if (accessRequired) {
    return {
      result: "prompt",
      prompt: promptFromMembership(item, ids[0], {}),
    };
  }
  if (lastError) {
    showError(lastError);
  }
  return { result: "noop" };
}

export async function requestMembershipViewAccess(
  prompt: MembershipViewAccessPrompt
): Promise<boolean> {
  const token = getAuthCookie("sessionToken") || "";
  if (!token) {
    showError("Please sign in again to request access.");
    return false;
  }
  if (!prompt.brokerageOrganizationId || !prompt.membershipId) {
    showError("This membership is missing the brokerage details needed to request access.");
    return false;
  }

  const apiBase = String(process.env.REACT_APP_API_URL || "").replace(/\/+$/, "");
  const url = `${apiBase}/organizations/${encodeURIComponent(
    prompt.brokerageOrganizationId
  )}/${encodeURIComponent(prompt.membershipId)}/view-access-request`;

  try {
    const { ok, data } = await fetchJson(url, token, "POST");
    if (!ok) {
      showError(data?.error || data?.message || "Failed to request membership access.");
      return false;
    }
    await markAsReadApi(prompt.notificationId);
    showSuccess(data?.message || "Access request sent.");
    return true;
  } catch (error: any) {
    showError(error?.message || "Failed to request membership access.");
    return false;
  }
}
